import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Plus, X } from 'lucide-react';
import { listCustomers, lookupCustomersByPhone, type CustomerDeviceHistoryItem, type CustomerListItem } from '@/lib/api/customers.js';
import { listDeviceMakes, listDeviceModels, createDeviceMake, createDeviceModel, type DeviceCategory, type DeviceModel } from '@/lib/api/devices.js';
import { ImeiLookupPanel } from '@/components/devices/ImeiLookupPanel.js';
import {
  CustomerPreviousDevicesPanel,
} from '@/components/tickets/CustomerPreviousDevicesPanel.js';
import { SnippetTextarea } from '@/components/common/SnippetTextarea.js';
import { listRepairServices, type RepairService } from '@/lib/api/repair-services.js';
import { createTicket, updateTicket } from '@/lib/api/tickets.js';
import { savePredictiveAlerts } from '@/lib/predictive-alerts-storage.js';
import { createCustomerDisplaySession, getDisplayConnectionStatus } from '@/lib/api/display-staff.js';
import { useDisplayTicketDraftMirror } from '@/hooks/useDisplayTicketDraftMirror.js';
import {
  buildTicketDraftDisplayEvent,
  hasTicketDraftContent,
} from '@/lib/build-ticket-draft-display.js';
import { getCurrentStore } from '@/lib/api/stores.js';
import { DISPLAY_STAFF_WS_EVENT } from '@/hooks/useDisplayStaffBridge.js';
import { useAuthStore } from '@/stores/auth.js';
import { isManagerRole } from '@/lib/permissions.js';
import { CustomerStatusGate } from '@/components/common/CustomerStatusGate.js';
import { CustomerStatusBadge } from '@/components/common/CustomerStatusBadge.js';
import { UpsellSuggestionsPanel } from '@/components/upsell/UpsellSuggestionsPanel.js';
import type { UpsellSuggestionResult } from '@/lib/api/customer-status-upsell.js';
import { CollectDepositModal } from '@/components/tickets/CollectDepositModal.js';
import { PromisedTimePanel } from '@/components/tickets/PromisedTimePanel.js';
import { canManageStoreRole, depositRemaining } from '@/lib/deposit.js';
import { formatCurrency } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastInfo, toastSuccess } from '@/lib/toast.js';
import { printLabelAfterCreate } from '@/lib/print.js';
import { usePrintNodeConfigured } from '@/hooks/usePrintNodeConfigured.js';
import { useManagerOverride } from '@/hooks/useManagerOverride.js';
import { ManagerOverrideModal } from '@/components/common/ManagerOverrideModal.js';
import { usePermissions } from '@/hooks/usePermissions.js';

const accessories = ['Case', 'Charger', 'Cable', 'SIM tray', 'Screen protector'];
const deviceTypes = [
  { value: 'phone', label: 'Phone' },
  { value: 'tablet', label: 'Tablet' },
  { value: 'laptop', label: 'Computer' },
  { value: 'watch', label: 'Watch' },
  { value: 'other', label: 'Other' },
];
const passcodeTypes = [
  { value: 'pin', label: 'PIN' },
  { value: 'password', label: 'Password' },
  { value: 'face_id', label: 'Face ID' },
  { value: 'fingerprint', label: 'Fingerprint' },
  { value: 'none', label: 'None' },
  { value: 'unknown', label: 'Unknown' },
];

interface DeviceDraft {
  category: string;
  makeId: string;
  makeName: string;
  modelId: string;
  modelName: string;
  modelSearch: string;
  customModelName: string;
  condition: string;
  passcodeType: string;
  passcode: string;
  accessories: string[];
  complaint: string;
  imei?: string;
  color?: string;
  serialNumber?: string;
  fromHistory?: boolean;
}

type RepairSelections = Record<number, Record<string, { service: RepairService; price: string }>>;

function emptyDevice(): DeviceDraft {
  return {
    category: 'phone',
    makeId: '',
    makeName: '',
    modelId: '',
    modelName: '',
    modelSearch: '',
    customModelName: '',
    condition: 'good',
    passcodeType: 'unknown',
    passcode: '',
    accessories: [],
    complaint: '',
    imei: '',
    color: '',
    serialNumber: '',
    fromHistory: false,
  };
}

function draftDeviceKey(device: DeviceDraft): string {
  return [
    device.makeId || 'null',
    device.modelId || 'null',
    (device.customModelName || device.modelName || '').trim().toLowerCase(),
  ].join('|');
}

function serviceDefaultPrice(service: RepairService): string {
  if (service.resolvedPrice != null && service.resolvedPrice !== '') return String(service.resolvedPrice);
  return service.defaultPrice != null && service.defaultPrice !== '' ? String(service.defaultPrice) : '0';
}

function deviceLabel(device: DeviceDraft, index: number): string {
  return [device.makeName, device.modelName || device.customModelName].filter(Boolean).join(' ') || `Device ${index + 1}`;
}

export function NewTicketPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [promisedDate, setPromisedDate] = useState(() => {
    const due = new Date(Date.now() + 90 * 60 * 1000);
    const offsetMs = due.getTimezoneOffset() * 60 * 1000;
    return new Date(due.getTime() - offsetMs).toISOString().slice(0, 16);
  });
  const [customerSearch, setCustomerSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerListItem | null>(null);
  const [flagAcked, setFlagAcked] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerPhone, setNewCustomerPhone] = useState('');
  const [devices, setDevices] = useState<DeviceDraft[]>([emptyDevice()]);
  const [activeDeviceIndex, setActiveDeviceIndex] = useState(0);
  const [showManualDevice, setShowManualDevice] = useState(false);
  const [historyDisplayName, setHistoryDisplayName] = useState<string | null>(null);
  const [repairs, setRepairs] = useState<RepairSelections>({});
  const [customRepairName, setCustomRepairName] = useState('');
  const [customRepairPrice, setCustomRepairPrice] = useState('0');
  const [customRepairMinutes, setCustomRepairMinutes] = useState('60');
  const [editingCustomId, setEditingCustomId] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [intakeNotes, setIntakeNotes] = useState('');
  const [waitingDisplayCustomer, setWaitingDisplayCustomer] = useState(false);
  const [sendingDisplayCustomer, setSendingDisplayCustomer] = useState(false);
  const storeId = useAuthStore((s) => s.user?.storeId);
  const user = useAuthStore((s) => s.user);
  const canQuickAddDevice = isManagerRole(user?.role ?? '');
  const [makeSearchOpen, setMakeSearchOpen] = useState(false);
  const [modelSearchOpen, setModelSearchOpen] = useState(false);
  const { flags } = usePermissions();
  const { requireOverride, modalProps: managerOverrideModal } = useManagerOverride();
  const canWaiveDeposit =
    Boolean(flags.can_waive_deposit) || canManageStoreRole(user?.role);
  const skipIdleOnUnmountRef = useRef(false);
  const pendingLabelPrintRef = useRef(false);
  const queryClient = useQueryClient();
  const storeQuery = useQuery({
    queryKey: ['current-store'],
    queryFn: () => getCurrentStore(),
  });
  const { hasPrintNode: hasLabelPrinter } = usePrintNodeConfigured('label');

  const [depositAfterCreate, setDepositAfterCreate] = useState<{
    id: string;
    ticketNumber: string;
    maxAmount: number;
  } | null>(null);

  useEffect(() => {
    if (!waitingDisplayCustomer) return;
    const handler = (ev: Event) => {
      const msg = (ev as CustomEvent<{ type?: string; payload?: Record<string, string> }>).detail;
      if (msg?.type !== 'display.customer_info_submitted') return;
      const p = msg.payload ?? {};
      const name = [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
      if (name) setNewCustomerName(name);
      if (p.phone) setNewCustomerPhone(p.phone);
      setSelectedCustomer(null); setFlagAcked(false);
      setWaitingDisplayCustomer(false);
      toastSuccess('Customer info received — fields populated ✓');
    };
    window.addEventListener(DISPLAY_STAFF_WS_EVENT, handler as EventListener);
    return () => window.removeEventListener(DISPLAY_STAFF_WS_EVENT, handler as EventListener);
  }, [waitingDisplayCustomer]);

  async function sendCustomerToDisplay() {
    if (!storeId) {
      toastError('No store selected.');
      return;
    }
    setSendingDisplayCustomer(true);
    try {
      const status = await getDisplayConnectionStatus(storeId);
      if (!status.connected) {
        toastError('Customer display not connected. Open the display URL on the facing iPad.');
        return;
      }
      await createCustomerDisplaySession();
      setWaitingDisplayCustomer(true);
      toastSuccess('Waiting for customer to enter info on display…');
    } catch (err) {
      const parsed = parseApiError(err);
      toastError(parsed.message, parsed.code);
    } finally {
      setSendingDisplayCustomer(false);
    }
  }

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(customerSearch), 300);
    return () => window.clearTimeout(t);
  }, [customerSearch]);

  const customerSearchTrimmed = debouncedSearch.trim();
  const phoneSearchDigits = customerSearchTrimmed.replace(/\D/g, '');
  const isPhoneSearch =
    phoneSearchDigits.length >= 7 &&
    phoneSearchDigits.length / Math.max(customerSearchTrimmed.replace(/\s/g, '').length, 1) >= 0.7;

  const customerQuery = useQuery({
    queryKey: ['customers', customerSearchTrimmed],
    queryFn: () => listCustomers({ q: customerSearchTrimmed, page: 1, pageSize: 8 }),
    enabled: customerSearchTrimmed.length >= 2 && !isPhoneSearch,
  });

  const phonePeersQuery = useQuery({
    queryKey: ['customers-by-phone', phoneSearchDigits],
    queryFn: () => lookupCustomersByPhone(phoneSearchDigits),
    enabled: isPhoneSearch,
  });

  const phonePeerResults = phonePeersQuery.data ?? [];
  const showPhonePicker = isPhoneSearch && phonePeerResults.length >= 1;

  const makesQuery = useQuery({
    queryKey: ['device-makes'],
    queryFn: () => listDeviceMakes(),
  });

  const modelsQuery = useQuery({
    queryKey: ['device-models'],
    queryFn: () => listDeviceModels(),
  });

  const currentDevice = devices[activeDeviceIndex]!;

  const servicesQuery = useQuery({
    queryKey: ['repair-services', currentDevice.makeId, currentDevice.modelId],
    queryFn: () =>
      listRepairServices({
        makeId: currentDevice.makeId ?? undefined,
        modelId: currentDevice.modelId ?? undefined,
        displayOnPos: true,
        requireModelPricing: Boolean(currentDevice.modelId),
      }),
    enabled: Boolean(currentDevice.modelId),
  });

  const flagBlocked =
    selectedCustomer?.customerStatus === 'flagged' && !flagAcked;

  const selectedRepairs = useMemo(
    () =>
      Object.entries(repairs).flatMap(([deviceIndex, deviceRepairs]) =>
        Object.values(deviceRepairs).map(({ service, price }) => ({
          deviceIndex: Number(deviceIndex),
          serviceId: /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
            service.id,
          )
            ? service.id
            : undefined,
          serviceName: service.name,
          quotedPrice: Number(price || 0),
          warrantyDays: service.defaultWarrantyDays ?? undefined,
          warrantyType: service.defaultWarrantyType ?? undefined,
        })),
      ),
    [repairs],
  );

  const total = selectedRepairs.reduce((sum, repair) => sum + repair.quotedPrice, 0);

  function buildPayload() {
    return {
      intakeType: 'drop_off',
      source: 'walk_in',
      priority: 'normal',
      promisedDate: promisedDate ? new Date(promisedDate).toISOString() : undefined,
      customerId: selectedCustomer?.id,
      customerNameOverride: selectedCustomer ? undefined : newCustomerName.trim() || undefined,
      customerPhoneOverride: selectedCustomer ? undefined : newCustomerPhone.trim() || undefined,
      customerComplaint:
        devices
          .map((d) => d.complaint)
          .filter(Boolean)
          .join('\n\n')
          .slice(0, 500) || undefined,
      intakeNotes: intakeNotes.trim() || undefined,
      devices: devices.map((device) => ({
        makeId: device.makeId || undefined,
        modelId: device.modelId || undefined,
        customModelName:
          device.modelId
            ? undefined
            : [device.makeName, device.customModelName].filter(Boolean).join(' ') || undefined,
        conditionAtIntake: device.condition,
        passcodeType: device.passcodeType,
        passcode:
          device.passcodeType === 'none' || device.passcodeType === 'unknown'
            ? undefined
            : device.passcode || undefined,
        accessoriesReceived: device.accessories,
        imei: device.imei?.replace(/\D/g, '').trim() || undefined,
        color: device.color?.trim() || undefined,
        serialNumber: device.serialNumber?.trim() || undefined,
      })),
      repairs: selectedRepairs,
      tags: [],
    };
  }

  function maybePrintLabelAfterIntake(ticketId: string) {
    if (!pendingLabelPrintRef.current) return;
    pendingLabelPrintRef.current = false;
    void printLabelAfterCreate(ticketId, hasLabelPrinter).then(
      () => toastSuccess('Ticket created — label printing...'),
      (e: unknown) => {
        const p = parseApiError(e);
        toastError(p.message, p.code);
      },
    );
  }

  const createMutation = useMutation({
    mutationFn: () => {
      const payload = buildPayload();
      return createTicket(payload);
    },
    onSuccess: (ticket) => {
      if (ticket.predictiveAlerts?.length) {
        savePredictiveAlerts(ticket.ticket.id, ticket.predictiveAlerts);
      }
      const remaining = depositRemaining(
        ticket.ticket.depositAmount,
        ticket.ticket.depositCollected,
      );
      if (ticket.ticket.depositRequired && remaining > 0) {
        setDepositAfterCreate({
          id: ticket.ticket.id,
          ticketNumber: ticket.ticket.ticketNumber,
          maxAmount: remaining,
        });
        return;
      }
      skipIdleOnUnmountRef.current = true;
      const tid = ticket.ticket.id;
      toastSuccess(`Ticket ${ticket.ticket.ticketNumber} created successfully`);
      maybePrintLabelAfterIntake(tid);
      navigate(tid ? `/tickets/${tid}` : '/tickets', tid ? { state: { openPreWaiver: true } } : undefined);
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const draftCustomerName = selectedCustomer
    ? [selectedCustomer.firstName, selectedCustomer.lastName].filter(Boolean).join(' ')
    : newCustomerName.trim() || null;

  const draftDevices = useMemo(
    () => devices.map((device, index) => ({ label: deviceLabel(device, index) })),
    [devices],
  );

  const draftDisplayEvent = useMemo(() => {
    const draftInput = {
      customerName: draftCustomerName,
      devices: draftDevices,
      repairs: selectedRepairs,
    };
    if (!hasTicketDraftContent(draftInput)) return null;
    return buildTicketDraftDisplayEvent({
      storeName: storeQuery.data?.name ?? 'Store',
      logoUrl: storeQuery.data?.logoUrl ?? null,
      customerName: draftCustomerName,
      devices: draftDevices,
      repairs: selectedRepairs,
    });
  }, [
    draftCustomerName,
    draftDevices,
    selectedRepairs,
    storeQuery.data?.name,
    storeQuery.data?.logoUrl,
  ]);

  useDisplayTicketDraftMirror({
    storeId: storeId ?? storeQuery.data?.id,
    enabled: !waitingDisplayCustomer && !createMutation.isPending,
    event: draftDisplayEvent,
    skipIdleOnUnmountRef,
  });

  function updateDevice(index: number, patch: Partial<DeviceDraft>) {
    setDevices((current) => current.map((device, i) => (i === index ? { ...device, ...patch } : device)));
  }

  function applyHistoryDevice(device: CustomerDeviceHistoryItem) {
    updateDevice(activeDeviceIndex, {
      makeId: device.makeId ?? '',
      makeName: device.makeName ?? '',
      modelId: device.modelId ?? '',
      modelName: device.modelName ?? '',
      modelSearch: device.modelName ?? '',
      customModelName: device.modelId ? '' : (device.customModelName ?? ''),
      imei: device.lastImei ?? '',
      color: device.lastColor ?? '',
      serialNumber: device.lastSerial ?? '',
      passcodeType: device.lastPasscodeType ?? 'unknown',
      condition: '',
      fromHistory: true,
      category: device.deviceCategory || 'phone',
    });
    setHistoryDisplayName(device.displayName);
    setShowManualDevice(false);
  }

  function clearHistorySelection() {
    updateDevice(activeDeviceIndex, emptyDevice());
    setHistoryDisplayName(null);
    setShowManualDevice(false);
  }

  function startManualDevice() {
    updateDevice(activeDeviceIndex, emptyDevice());
    setHistoryDisplayName(null);
    setShowManualDevice(true);
  }

  const excludeHistoryKeys = useMemo(
    () =>
      devices
        .map((d, i) => (i === activeDeviceIndex ? null : draftDeviceKey(d)))
        .filter((k): k is string => Boolean(k) && k !== 'null|null|'),
    [devices, activeDeviceIndex],
  );

  const showHistoryPanel =
    Boolean(selectedCustomer?.id) &&
    !showManualDevice &&
    !devices[activeDeviceIndex]?.fromHistory;

  function selectMake(index: number, makeId: string) {
    const make = makesQuery.data?.find((m) => m.id === makeId);
    updateDevice(index, {
      makeId,
      makeName: make?.name ?? '',
      modelId: '',
      modelName: '',
      modelSearch: '',
      customModelName: '',
    });
    setMakeSearchOpen(false);
    setModelSearchOpen(false);
  }

  function selectModel(index: number, modelId: string, models: DeviceModel[]) {
    const model = models.find((m) => m.id === modelId);
    updateDevice(index, {
      modelId,
      modelName: model?.name ?? '',
      modelSearch: model?.name ?? '',
      customModelName: '',
    });
    setModelSearchOpen(false);
  }

  async function quickAddMake(index: number, name: string) {
    try {
      const created = await createDeviceMake({
        name,
        category: (devices[index]?.category as DeviceCategory) || 'phone',
      });
      await queryClient.invalidateQueries({ queryKey: ['device-makes'] });
      updateDevice(index, {
        makeId: created.id,
        makeName: created.name,
        modelId: '',
        modelName: '',
        modelSearch: '',
        customModelName: '',
      });
      setMakeSearchOpen(false);
      toastSuccess(`${created.name} added to catalog`);
    } catch (err) {
      toastError(parseApiError(err).message);
    }
  }

  async function quickAddModel(index: number, name: string) {
    const device = devices[index];
    if (!device?.makeId) return;
    try {
      const make = makesQuery.data?.find((m) => m.id === device.makeId);
      const created = await createDeviceModel({
        makeId: device.makeId,
        name,
        category:
          (make?.category as DeviceCategory) ||
          (device.category as DeviceCategory) ||
          'phone',
      });
      await queryClient.invalidateQueries({ queryKey: ['device-models'] });
      updateDevice(index, {
        modelId: created.id,
        modelName: created.name,
        modelSearch: created.name,
        customModelName: '',
      });
      setModelSearchOpen(false);
      toastSuccess(`${created.name} added to catalog`);
    } catch (err) {
      toastError(parseApiError(err).message);
    }
  }

  function toggleAccessory(index: number, accessory: string) {
    const current = devices[index]!;
    const next = current.accessories.includes(accessory)
      ? current.accessories.filter((a) => a !== accessory)
      : [...current.accessories, accessory];
    updateDevice(index, { accessories: next });
  }

  function toggleService(service: RepairService) {
    setRepairs((current) => {
      const selected = current[activeDeviceIndex]?.[service.id];
      const nextForDevice = { ...(current[activeDeviceIndex] ?? {}) };
      if (selected) {
        delete nextForDevice[service.id];
      } else {
        nextForDevice[service.id] = { service, price: serviceDefaultPrice(service) };
      }
      return { ...current, [activeDeviceIndex]: nextForDevice };
    });
  }

  function setRepairPrice(serviceId: string, price: string) {
    setRepairs((current) => ({
      ...current,
      [activeDeviceIndex]: {
        ...(current[activeDeviceIndex] ?? {}),
        [serviceId]: {
          ...current[activeDeviceIndex]![serviceId]!,
          price,
        },
      },
    }));
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const submitter = (event.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null;
    if (step < 4) {
      if (step === 1 && flagBlocked) return;
      if (submitter?.dataset.action !== 'continue') return;
      setStep((s) => s + 1);
      return;
    }
    const errors: Record<string, string> = {};
    const messages: string[] = [];
    devices.forEach((device, index) => {
      const hasDeviceIdentity = Boolean(device.makeId || device.modelId || device.customModelName.trim());
      if (!hasDeviceIdentity) {
        const message = `Device ${index + 1}: Please select a make or enter a model name`;
        errors[`device-${index}-make`] = message;
        messages.push(message);
      }
      if (!device.condition) {
        const message = `Device ${index + 1}: Condition is required`;
        errors[`device-${index}-condition`] = message;
        messages.push(message);
      }
    });
    selectedRepairs.forEach((repair, index) => {
      // $0 is valid (warranty, goodwill, covered-under-protection, etc.)
      if (!Number.isFinite(repair.quotedPrice) || repair.quotedPrice < 0) {
        const message = `Repair ${index + 1}: Price cannot be negative`;
        errors[`repair-${index}-price`] = message;
        messages.push(message);
      }
    });
    if (!selectedCustomer && !newCustomerName.trim()) {
      errors.customer = 'Customer: Please select or create a customer';
      messages.push(errors.customer);
    }
    if (selectedRepairs.length === 0) {
      errors.repairs = 'Repair 1: Please select at least one repair';
      messages.push(errors.repairs);
    }
    setFieldErrors(errors);
    if (messages.length) {
      toastError(messages.join('\n'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    createMutation.mutate();
  }

  const customerName = selectedCustomer
    ? [selectedCustomer.firstName, selectedCustomer.lastName].filter(Boolean).join(' ')
    : newCustomerName;

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <button
        type="button"
        onClick={() => navigate('/tickets')}
        className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-950"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to queue
      </button>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-6 flex items-center gap-3">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="flex flex-1 items-center gap-3">
              <div
                className={[
                  'flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold',
                  step >= n ? 'bg-primary text-white' : 'bg-slate-100 text-slate-500',
                ].join(' ')}
              >
                {step > n ? <Check className="h-4 w-4" /> : n}
              </div>
              {n < 4 ? <div className="h-px flex-1 bg-slate-200" /> : null}
            </div>
          ))}
        </div>

        <form className="space-y-5" onSubmit={submit}>
          {step === 1 ? (
            <section className="space-y-4">
              <div>
                <h2 className="text-xl font-semibold text-slate-950">Customer</h2>
            <CustomerStatusGate
              customer={selectedCustomer}
              context="new_ticket"
              vipLabel={storeQuery.data?.vipLabel ?? null}
              flaggedLabel={storeQuery.data?.flaggedLabel ?? null}
              onFlagAcknowledged={() => setFlagAcked(true)}
            />

                <p className="text-sm text-slate-500">
                  Search for an existing customer or enter their details below. New customers are saved
                  automatically when you create the ticket.
                </p>
              </div>
              <button
                type="button"
                disabled={sendingDisplayCustomer || Boolean(selectedCustomer)}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-primary/40 bg-primary/5 px-4 py-3 text-sm font-semibold text-primary hover:bg-primary/10 disabled:opacity-50 sm:w-auto"
                onClick={() => void sendCustomerToDisplay()}
              >
                📺 Customer enters info on display
              </button>
              {waitingDisplayCustomer ? (
                <p className="flex items-center gap-2 text-sm text-slate-600">
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  Waiting for customer on display…
                </p>
              ) : null}
              <input
                type="search"
                className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  setSelectedCustomer(null); setFlagAcked(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.preventDefault();
                }}
                placeholder="Search by name, phone, or email"
              />
              {customerSearch.trim().length > 0 && customerSearch.trim().length < 2 ? (
                <p className="text-sm text-slate-500">Type at least 2 characters to search.</p>
              ) : null}
              {customerQuery.isFetching ? (
                <p className="text-sm text-slate-500">Searching…</p>
              ) : null}
              {customerQuery.isError ? (
                <p className="text-sm text-red-600">Could not load customers. Try again.</p>
              ) : null}
              {customerSearchTrimmed.length >= 2 &&
              !customerQuery.isFetching &&
              !phonePeersQuery.isFetching &&
              !showPhonePicker &&
              customerQuery.data &&
              !customerQuery.data.data.length ? (
                <p className="text-sm text-slate-500">No customers found.</p>
              ) : null}
              {showPhonePicker ? (
                <div className="rounded-xl border border-primary/30 bg-primary/5 p-3">
                  <p className="text-sm font-semibold text-slate-900">
                    {phonePeerResults.length} customer
                    {phonePeerResults.length === 1 ? '' : 's'} with this number
                  </p>
                  <div className="mt-2 space-y-1">
                    {phonePeerResults.map((customer) => (
                      <label
                        key={customer.id}
                        className={[
                          'flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2',
                          selectedCustomer?.id === customer.id ? 'border-primary ring-2 ring-primary/20' : '',
                        ].join(' ')}
                      >
                        <input
                          type="radio"
                          name="phone-peer"
                          className="mt-1"
                          checked={selectedCustomer?.id === customer.id}
                          onChange={() => {
                            setSelectedCustomer({
                              id: customer.id,
                              firstName: customer.firstName,
                              lastName: customer.lastName ?? '',
                              email: customer.email,
                              phone: customer.phone,
                              totalTickets: customer.totalTickets,
                              totalSpend: '0',
                              lastVisitAt: customer.lastVisitAt,
                              customerStatus: customer.customerStatus as CustomerListItem['customerStatus'],
                            });
                            setFlagAcked(false);
                            setCustomerSearch(
                              [customer.firstName, customer.lastName].filter(Boolean).join(' '),
                            );
                            setShowManualDevice(false);
                            setHistoryDisplayName(null);
                          }}
                        />
                        <span className="text-sm">
                          <span className="font-semibold text-slate-950">
                            {[customer.firstName, customer.lastName].filter(Boolean).join(' ')}
                          </span>
                          <span className="mt-0.5 block text-xs text-slate-500">
                            {customer.totalTickets} tickets
                            {customer.lastVisitAt
                              ? ` · last ${new Date(customer.lastVisitAt).toLocaleDateString()}`
                              : ''}
                          </span>
                        </span>
                      </label>
                    ))}
                    <button
                      type="button"
                      className="mt-1 w-full rounded-lg border border-dashed border-slate-300 px-3 py-2 text-left text-sm font-semibold text-slate-700 hover:bg-white"
                      onClick={() => {
                        setSelectedCustomer(null);
                        setFlagAcked(false);
                        setNewCustomerPhone(customerSearchTrimmed);
                        setCustomerSearch('');
                      }}
                    >
                      + New customer with this number
                    </button>
                  </div>
                </div>
              ) : null}
              {!showPhonePicker && customerQuery.data?.data.length ? (
                <div className="rounded-xl border border-slate-200">
                  {customerQuery.data.data.map((customer) => (
                    <button
                      key={customer.id}
                      type="button"
                      className={[
                        'flex w-full items-center justify-between border-b border-slate-100 px-4 py-3 text-left last:border-b-0 hover:bg-slate-50',
                        selectedCustomer?.id === customer.id ? 'bg-primary/5' : '',
                      ].join(' ')}
                      onClick={() => {
                        setSelectedCustomer(customer); setFlagAcked(false);
                        setCustomerSearch([customer.firstName, customer.lastName].filter(Boolean).join(' '));
                        setShowManualDevice(false);
                        setHistoryDisplayName(null);
                      }}
                    >
                      <span>
                        <span className="flex flex-wrap items-center gap-2 font-semibold text-slate-950">
                          {[customer.firstName, customer.lastName].filter(Boolean).join(' ')}
                          <CustomerStatusBadge
                            status={customer.customerStatus}
                            vipLabel={storeQuery.data?.vipLabel ?? null}
                            flaggedLabel={storeQuery.data?.flaggedLabel ?? null}
                            reason={customer.statusReason ?? null}
                          />
                        </span>
                        <span className="text-sm text-slate-500">{customer.phone ?? customer.email}</span>
                      </span>
                      {selectedCustomer?.id === customer.id ? <Check className="h-4 w-4 text-primary" /> : null}
                    </button>
                  ))}
                </div>
              ) : null}
              <div className="rounded-2xl bg-slate-50 p-4">
                <div className="mb-3 text-sm font-semibold text-slate-900">New Customer</div>
                {fieldErrors.customer ? (
                  <p className="mb-3 text-sm font-semibold text-red-600">{fieldErrors.customer}</p>
                ) : null}
                <div className="grid gap-3 sm:grid-cols-2">
                  <input
                    className="rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary"
                    placeholder="Full name"
                    value={newCustomerName}
                    onChange={(e) => setNewCustomerName(e.target.value)}
                    disabled={Boolean(selectedCustomer)}
                  />
                  <input
                    className="rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary"
                    placeholder="Phone"
                    value={newCustomerPhone}
                    onChange={(e) => setNewCustomerPhone(e.target.value)}
                    disabled={Boolean(selectedCustomer)}
                  />
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Phone is optional but recommended — we match existing customers and enable SMS
                  notifications.
                </p>
              </div>
            </section>
          ) : null}

          {step === 2 ? (
            <section className="space-y-5">
              <div>
                <h2 className="text-xl font-semibold text-slate-950">Devices</h2>
                <p className="text-sm text-slate-500">Add one or more devices for this customer.</p>
              </div>
              {showHistoryPanel ? (
                <CustomerPreviousDevicesPanel
                  customerId={selectedCustomer!.id}
                  customerFirstName={selectedCustomer?.firstName ?? null}
                  excludeKeys={excludeHistoryKeys}
                  onSelect={applyHistoryDevice}
                  onNewDevice={startManualDevice}
                />
              ) : null}
              {devices[activeDeviceIndex]?.fromHistory && historyDisplayName ? (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-950">
                  <div>
                    <div className="font-semibold">✓ Using {historyDisplayName}</div>
                    <div className="text-emerald-800/80">
                      Details pre-filled from last visit. Please verify and update as needed.
                    </div>
                  </div>
                  <button
                    type="button"
                    className="rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold"
                    onClick={clearHistorySelection}
                  >
                    Change →
                  </button>
                </div>
              ) : null}
              {devices.map((device, index) => {
                const matchingModels = (modelsQuery.data ?? []).filter(
                  (model) =>
                    (!device.makeId || model.makeId === device.makeId) &&
                    (!device.category || model.category === device.category),
                );

                return (
                <div key={index} className="rounded-2xl border border-slate-200 p-4">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="font-semibold text-slate-950">{deviceLabel(device, index)}</h3>
                    {devices.length > 1 ? (
                      <button
                        type="button"
                        className="rounded-lg p-2 text-red-600 hover:bg-red-50"
                        onClick={() => {
                          setDevices((current) => current.filter((_, i) => i !== index));
                          setActiveDeviceIndex(0);
                          setRepairs({});
                        }}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    ) : null}
                  </div>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <label className="block">
                      <span className="text-sm font-medium text-slate-700">Device Type</span>
                      <select
                        className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary"
                        value={device.category}
                        onChange={(e) =>
                          updateDevice(index, {
                            category: e.target.value,
                            modelId: '',
                            modelName: '',
                            customModelName: '',
                          })
                        }
                      >
                        {deviceTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-sm font-medium text-slate-700">Manufacturer</span>
                      <div className="relative mt-1">
                        <input
                          className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary"
                          value={device.makeName}
                          placeholder="Search make..."
                          onFocus={() => {
                            setActiveDeviceIndex(index);
                            setMakeSearchOpen(true);
                          }}
                          onChange={(e) => {
                            setActiveDeviceIndex(index);
                            setMakeSearchOpen(true);
                            updateDevice(index, {
                              makeId: '',
                              makeName: e.target.value,
                              modelId: '',
                              modelName: '',
                              modelSearch: '',
                              customModelName: '',
                            });
                          }}
                          required
                        />
                        {makeSearchOpen && activeDeviceIndex === index && device.makeName.trim() && !device.makeId ? (
                          <div className="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg">
                            {(makesQuery.data ?? [])
                              .filter((m) =>
                                m.name.toLowerCase().includes(device.makeName.trim().toLowerCase()),
                              )
                              .slice(0, 8)
                              .map((make) => (
                                <button
                                  key={make.id}
                                  type="button"
                                  className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                                  onClick={() => selectMake(index, make.id)}
                                >
                                  {make.name}
                                </button>
                              ))}
                            {(makesQuery.data ?? []).every(
                              (m) => m.name.toLowerCase() !== device.makeName.trim().toLowerCase(),
                            ) ? (
                              canQuickAddDevice ? (
                                <button
                                  type="button"
                                  className="block w-full border-t border-slate-100 px-3 py-2.5 text-left text-sm font-semibold text-primary hover:bg-primary/5"
                                  onClick={() => void quickAddMake(index, device.makeName.trim())}
                                >
                                  ＋ Add &quot;{device.makeName.trim()}&quot; as manufacturer
                                </button>
                              ) : (
                                <div className="border-t border-slate-100 px-3 py-2.5 text-sm text-slate-500">
                                  Not in catalog — ask a manager to add it
                                </div>
                              )
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </label>
                    <label className="block">
                      <span className="text-sm font-medium text-slate-700">Model</span>
                      <div className="relative mt-1">
                        <input
                          className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary disabled:bg-slate-50"
                          value={device.modelSearch || device.modelName || device.customModelName}
                          placeholder={device.makeId ? 'Search model...' : 'Select a make first'}
                          disabled={!device.makeId}
                          onFocus={() => {
                            setActiveDeviceIndex(index);
                            setModelSearchOpen(true);
                          }}
                          onChange={(e) => {
                            setActiveDeviceIndex(index);
                            setModelSearchOpen(true);
                            updateDevice(index, {
                              modelSearch: e.target.value,
                              modelId: '',
                              modelName: '',
                              customModelName: e.target.value,
                            });
                          }}
                          required
                        />
                        {modelSearchOpen &&
                        activeDeviceIndex === index &&
                        device.makeId &&
                        (device.modelSearch || device.customModelName) &&
                        !device.modelId ? (
                          <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg">
                            {matchingModels
                              .filter((m) =>
                                m.name
                                  .toLowerCase()
                                  .includes(
                                    (device.modelSearch || device.customModelName).trim().toLowerCase(),
                                  ),
                              )
                              .slice(0, 12)
                              .map((model) => (
                                <button
                                  key={model.id}
                                  type="button"
                                  className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                                  onClick={() => selectModel(index, model.id, matchingModels)}
                                >
                                  {model.name}
                                </button>
                              ))}
                            {(() => {
                              const q = (device.modelSearch || device.customModelName).trim();
                              const filtered = matchingModels.filter((m) =>
                                m.name.toLowerCase().includes(q.toLowerCase()),
                              );
                              const exact = matchingModels.some(
                                (m) => m.name.toLowerCase() === q.toLowerCase(),
                              );
                              if (!q || exact) return null;
                              return (
                                <div className="border-t border-slate-100">
                                  {filtered.length === 0 ? (
                                    <div className="px-3 py-2 text-sm text-slate-500">
                                      No matching models
                                    </div>
                                  ) : null}
                                  {canQuickAddDevice ? (
                                    <button
                                      type="button"
                                      className="block w-full px-3 py-2.5 text-left text-sm font-semibold text-primary hover:bg-primary/5"
                                      onClick={() => void quickAddModel(index, q)}
                                    >
                                      ＋ Add &quot;{q}&quot; to {device.makeName || 'catalog'}
                                    </button>
                                  ) : (
                                    <div className="px-3 py-2.5 text-sm text-slate-500">
                                      Not in catalog — ask a manager to add it
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        ) : null}
                      </div>
                    </label>
                  </div>
                  {fieldErrors[`device-${index}-make`] ? (
                    <p className="mt-2 text-sm font-semibold text-red-600">{fieldErrors[`device-${index}-make`]}</p>
                  ) : null}
                  <div className="mt-4">
                    <span className="text-sm font-medium text-slate-700">Condition</span>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {['excellent', 'good', 'fair', 'poor', 'broken'].map((value) => (
                        <button key={value} type="button" onClick={() => updateDevice(index, { condition: value })} className={['rounded-xl px-3 py-2 text-sm font-semibold capitalize', device.condition === value ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600'].join(' ')}>
                          {value}
                        </button>
                      ))}
                    </div>
                    {fieldErrors[`device-${index}-condition`] ? (
                      <p className="mt-2 text-sm font-semibold text-red-600">{fieldErrors[`device-${index}-condition`]}</p>
                    ) : null}
                  </div>
                  <div className="mt-4">
                    <span className="text-sm font-medium text-slate-700">Passcode type</span>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {passcodeTypes.map((type) => (
                        <button key={type.value} type="button" onClick={() => updateDevice(index, { passcodeType: type.value, passcode: '' })} className={['rounded-xl px-3 py-2 text-sm font-semibold', device.passcodeType === type.value ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600'].join(' ')}>
                          {type.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {device.passcodeType !== 'none' && device.passcodeType !== 'unknown' ? (
                    <label className="mt-4 block">
                      <span className="text-sm font-medium text-slate-700">Unlock Code / PIN</span>
                      <input
                        className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary"
                        type="text"
                        autoComplete="off"
                        value={device.passcode}
                        onChange={(e) => updateDevice(index, { passcode: e.target.value })}
                      />
                    </label>
                  ) : null}
                  <div className="mt-4">
                    <span className="text-sm font-medium text-slate-700">Accessories</span>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {accessories.map((accessory) => {
                        const selected = device.accessories.includes(accessory);
                        return (
                          <button key={accessory} type="button" onClick={() => toggleAccessory(index, accessory)} className={['rounded-xl px-3 py-2 text-sm font-semibold', selected ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600'].join(' ')}>
                            {accessory}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="mt-4">
                    <span className="text-sm font-medium text-slate-700">IMEI</span>
                    <div className="mt-2">
                      <ImeiLookupPanel
                        value={device.imei ?? ''}
                        onChange={(imei) => updateDevice(index, { imei })}
                        onApplyDevice={(info) => {
                          const normalize = (s: string) =>
                            s.toLowerCase().replace(/[^a-z0-9]/g, '');
                          const matchedMake = (makesQuery.data ?? []).find(
                            (m) => m.name.toLowerCase() === info.make.toLowerCase(),
                          );
                          const makeId = matchedMake?.id ?? '';
                          const makeName = matchedMake?.name ?? info.make;
                          const modelsForMake = (modelsQuery.data ?? []).filter(
                            (m) => !makeId || m.makeId === makeId,
                          );
                          const needle = normalize(info.model);
                          const matchedModel =
                            modelsForMake.find((m) => normalize(m.name) === needle) ??
                            modelsForMake.find((m) => {
                              const n = normalize(m.name);
                              return n.length >= 4 && (needle.includes(n) || n.includes(needle));
                            });

                          if (matchedModel) {
                            updateDevice(index, {
                              makeId,
                              makeName,
                              modelId: matchedModel.id,
                              modelName: matchedModel.name,
                              modelSearch: matchedModel.name,
                              customModelName: '',
                              ...(matchedModel.category
                                ? { category: matchedModel.category }
                                : {}),
                            });
                          } else {
                            updateDevice(index, {
                              makeId,
                              makeName,
                              modelId: '',
                              modelName: '',
                              modelSearch: info.model || info.make,
                              customModelName: info.model || info.make,
                            });
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <label className="block">
                      <span className="text-sm font-medium text-slate-700">Color</span>
                      <input
                        className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary"
                        value={device.color ?? ''}
                        onChange={(e) => updateDevice(index, { color: e.target.value })}
                        placeholder="Color"
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm font-medium text-slate-700">Serial</span>
                      <input
                        className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary"
                        value={device.serialNumber ?? ''}
                        onChange={(e) => updateDevice(index, { serialNumber: e.target.value })}
                        placeholder="Serial number"
                      />
                    </label>
                  </div>
                  <label className="mt-4 block">
                    <span className="text-sm font-medium text-slate-700">Customer complaint</span>
                    <SnippetTextarea
                      className="mt-1 min-h-20 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary"
                      value={device.complaint}
                      maxLength={500}
                      onChange={(complaint) => updateDevice(index, { complaint: complaint.slice(0, 500) })}
                      placeholder="Describe the issue… type / to insert a snippet"
                    />
                  </label>
                </div>
                );
              })}
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                onClick={() => {
                  setDevices((current) => [...current, emptyDevice()]);
                  setActiveDeviceIndex(devices.length);
                  setShowManualDevice(false);
                  setHistoryDisplayName(null);
                }}
              >
                <Plus className="h-4 w-4" />
                Add Another Device
              </button>
            </section>
          ) : null}

          {step === 3 ? (
            <section className="space-y-4">
              <div>
                <h2 className="text-xl font-semibold text-slate-950">Repairs</h2>
                <p className="text-sm text-slate-500">Select repairs per device. Prices come from the repair services API.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {devices.map((device, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setActiveDeviceIndex(index)}
                    className={[
                      'rounded-xl px-3 py-2 text-sm font-semibold',
                      activeDeviceIndex === index ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600',
                    ].join(' ')}
                  >
                    {deviceLabel(device, index)}
                  </button>
                ))}
              </div>
              {fieldErrors.repairs ? (
                <p className="text-sm font-semibold text-red-600">{fieldErrors.repairs}</p>
              ) : null}
              {!currentDevice.modelId ? (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  Select a catalog device model above to see repair services with pricing for that device.
                </div>
              ) : null}
              {currentDevice.modelId && servicesQuery.isLoading ? (
                <p className="text-sm text-slate-500">Loading services...</p>
              ) : null}
              {currentDevice.modelId && !servicesQuery.isLoading && servicesQuery.data?.length === 0 ? (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  No repair services are configured for {deviceLabel(currentDevice, activeDeviceIndex)}. Add pricing
                  in Settings → Repair Services, or use a custom repair.
                </div>
              ) : null}
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {servicesQuery.data?.map((service) => {
                  const selected = repairs[activeDeviceIndex]?.[service.id];
                  return (
                    <div
                      key={service.id}
                      className={[
                        'rounded-2xl border p-4',
                        selected ? 'border-primary bg-primary/5' : 'border-slate-200 bg-white',
                      ].join(' ')}
                    >
                      <button type="button" className="w-full text-left" onClick={() => toggleService(service)}>
                        <div className="font-semibold text-slate-950">{service.name}</div>
                        <div className="mt-2 flex items-center justify-between gap-3">
                          <span className="text-sm font-semibold text-slate-700">
                            {formatCurrency(serviceDefaultPrice(service))}
                          </span>
                          <span
                            className={[
                              'rounded-full px-2 py-0.5 text-xs font-semibold',
                              service.hasPriceOverride
                                ? 'bg-green-100 text-green-700'
                                : 'bg-slate-100 text-slate-600',
                            ].join(' ')}
                          >
                            {service.hasPriceOverride ? 'Device price' : 'Default'}
                          </span>
                        </div>
                      </button>
                      {selected ? (
                        <label className="mt-3 block">
                          <span className="text-xs font-semibold text-slate-500">Price $</span>
                          <input
                            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary"
                            type="number"
                            min="0"
                            step="0.01"
                            value={selected.price}
                            onChange={(e) => setRepairPrice(service.id, e.target.value)}
                          />
                        </label>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              <div className="rounded-2xl border border-dashed border-slate-300 p-4">
                <div className="mb-3 font-semibold text-slate-950">Custom Repair</div>
                <div className="grid gap-3 sm:grid-cols-[1fr_120px_100px_auto]">
                  <input
                    className="rounded-xl border border-slate-300 px-3 py-2"
                    value={customRepairName}
                    onChange={(e) => setCustomRepairName(e.target.value)}
                    placeholder="Repair name"
                  />
                  <input
                    className="rounded-xl border border-slate-300 px-3 py-2"
                    type="number"
                    min="0"
                    step="0.01"
                    value={customRepairPrice}
                    onChange={(e) => setCustomRepairPrice(e.target.value)}
                    placeholder="Price"
                  />
                  <input
                    className="rounded-xl border border-slate-300 px-3 py-2"
                    type="number"
                    min="0"
                    value={customRepairMinutes}
                    onChange={(e) => setCustomRepairMinutes(e.target.value)}
                    placeholder="Mins"
                    title="Estimated minutes"
                  />
                  <button
                    type="button"
                    className="rounded-xl bg-primary px-4 py-2 font-semibold text-white"
                    onClick={() => {
                      if (!customRepairName.trim()) return;
                      const id = editingCustomId ?? `custom-${Date.now()}`;
                      const service = {
                        id,
                        name: customRepairName.trim(),
                        defaultPrice: customRepairPrice,
                        resolvedPrice: customRepairPrice,
                        estimatedMinutes: Number(customRepairMinutes) || null,
                      } as RepairService;
                      setRepairs((current) => ({
                        ...current,
                        [activeDeviceIndex]: {
                          ...(current[activeDeviceIndex] ?? {}),
                          [id]: { service, price: customRepairPrice },
                        },
                      }));
                      setCustomRepairName('');
                      setCustomRepairPrice('0');
                      setCustomRepairMinutes('60');
                      setEditingCustomId(null);
                    }}
                  >
                    {editingCustomId ? 'Save' : 'Add'}
                  </button>
                </div>
                {Object.entries(repairs[activeDeviceIndex] ?? {})
                  .filter(([id]) => id.startsWith('custom-'))
                  .map(([id, row]) => (
                    <div
                      key={id}
                      className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                    >
                      <div>
                        <span className="font-semibold text-slate-900">{row.service.name}</span>
                        <span className="ml-2 text-slate-600">{formatCurrency(row.price)}</span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="text-xs font-semibold text-primary"
                          onClick={() => {
                            setEditingCustomId(id);
                            setCustomRepairName(row.service.name);
                            setCustomRepairPrice(row.price);
                            setCustomRepairMinutes(
                              String(
                                (row.service as RepairService & { estimatedMinutes?: number })
                                  .estimatedMinutes ?? 60,
                              ),
                            );
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="text-xs font-semibold text-red-600"
                          onClick={() => {
                            setRepairs((current) => {
                              const next = { ...(current[activeDeviceIndex] ?? {}) };
                              delete next[id];
                              return { ...current, [activeDeviceIndex]: next };
                            });
                            if (editingCustomId === id) {
                              setEditingCustomId(null);
                              setCustomRepairName('');
                            }
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            </section>
          ) : null}

          {step === 3 && selectedCustomer && selectedRepairs.length > 0 ? (
              <UpsellSuggestionsPanel
                mode="inline"
                context={{
                  showAt: 'ticket_create',
                  customerId: selectedCustomer.id,
                  devices: devices.map((d) => ({
                    modelId: d.modelId ?? null,
                    makeName: d.makeName ?? null,
                    modelName: d.modelName ?? null,
                    customModelName: null,
                  })),
                  repairs: selectedRepairs.map((r) => ({
                    serviceName: r.serviceName,
                    quotedPrice: r.quotedPrice,
                  })),
                }}
                onAddService={(suggestion: UpsellSuggestionResult) => {
                  const cfg = suggestion.actionConfig ?? {};
                  const name =
                    (typeof cfg.serviceName === 'string' && cfg.serviceName) ||
                    suggestion.actionLabel ||
                    suggestion.title;
                  const price =
                    typeof cfg.price === 'number' ? cfg.price : Number(cfg.price ?? 0) || 0;
                  const key = `upsell-${suggestion.id}`;
                  setRepairs((current) => {
                    const deviceIndex = activeDeviceIndex ?? 0;
                    const deviceRepairs = { ...(current[deviceIndex] ?? {}) };
                    deviceRepairs[key] = {
                      service: {
                        id: key,
                        name,
                        defaultPrice: String(price),
                        resolvedPrice: String(price),
                        resolvedWarrantyDays: null,
                        defaultWarrantyDays: null,
                        hasPriceOverride: false,
                      } as never,
                      price: String(price),
                    };
                    return { ...current, [deviceIndex]: deviceRepairs };
                  });
                }}
              />
            ) : null}

        {step === 4 ? (
            <section className="space-y-5">
              <div>
                <h2 className="text-xl font-semibold text-slate-950">Review & Create</h2>
                <p className="text-sm text-slate-500">Confirm the ticket before creating it.</p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 p-4">
                  <div className="text-sm text-slate-500">Customer</div>
                  <div className="font-semibold text-slate-950">{customerName}</div>
                </div>
                <div className="rounded-2xl border border-slate-200 p-4">
                  <div className="text-sm text-slate-500">Devices</div>
                  <div className="font-semibold text-slate-950">{devices.length}</div>
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 p-4">
                <div className="mb-3 font-semibold text-slate-950">Repairs</div>
                {selectedRepairs.map((repair, index) => (
                  <div key={`${repair.serviceId}-${index}`} className="flex justify-between border-b border-slate-100 py-2 last:border-b-0">
                    <span>Device {repair.deviceIndex + 1}: {repair.serviceName}</span>
                    <span className="font-semibold">{formatCurrency(repair.quotedPrice)}</span>
                  </div>
                ))}
                <div className="mt-3 flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span>{formatCurrency(total)}</span>
                </div>
              </div>
              {devices.some((d) => d.complaint.trim()) ? (
                <div className="rounded-2xl border border-primary/20 bg-primary/[0.04] p-4">
                  <div className="text-xs font-bold uppercase tracking-wide text-primary">Customer complaint</div>
                  <p className="mt-2 whitespace-pre-wrap text-sm text-slate-800">
                    {devices
                      .map((d) => d.complaint)
                      .filter(Boolean)
                      .join('\n\n')}
                  </p>
                </div>
              ) : null}
              <PromisedTimePanel
                serviceIds={selectedRepairs
                  .map((r) => r.serviceId)
                  .filter((id): id is string => Boolean(id))}
                value={promisedDate}
                onChange={setPromisedDate}
              />
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Internal diagnostic notes</span>
                <SnippetTextarea
                  className="mt-1 min-h-20 w-full rounded-xl border border-amber-200 bg-amber-50/50 px-3 py-2 outline-none focus:border-amber-400"
                  value={intakeNotes}
                  onChange={setIntakeNotes}
                  placeholder="Staff observations — not shown to the customer"
                />
              </label>
            </section>
          ) : null}

          {createMutation.error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              Could not create ticket. Check required fields and try again.
            </div>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-5">
            <button
              type="button"
              disabled={step === 1}
              onClick={() => setStep((s) => Math.max(s - 1, 1))}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            >
              Back
            </button>
            <div className="flex flex-wrap justify-end gap-2">
              {step === 4 ? (
                <>
                  <button
                    type="submit"
                    data-action="continue"
                    disabled={createMutation.isPending || selectedRepairs.length === 0}
                    onClick={() => {
                      pendingLabelPrintRef.current = false;
                    }}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-60"
                  >
                    {createMutation.isPending ? 'Creating...' : 'Create Ticket'}
                  </button>
                  <button
                    type="submit"
                    data-action="continue"
                    disabled={createMutation.isPending || selectedRepairs.length === 0}
                    onClick={() => {
                      pendingLabelPrintRef.current = true;
                    }}
                    className="inline-flex items-center gap-2 rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-60"
                  >
                    {createMutation.isPending ? 'Creating...' : 'Create & Print Label'}
                  </button>
                </>
              ) : (
                <button
                  type="submit"
                  data-action="continue"
                  disabled={createMutation.isPending || (step === 4 && selectedRepairs.length === 0)}
                  className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-[#164477] disabled:opacity-60"
                >
                  {createMutation.isPending ? 'Creating...' : 'Continue'}
                  <ArrowRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
      {depositAfterCreate ? (
        <CollectDepositModal
          open
          variant="mandatory_intake"
          showManagerOverride={canWaiveDeposit}
          successToastMessage="Deposit collected — ticket created ✓"
          onManagerSkip={
            canWaiveDeposit
              ? async () => {
                  const result = await requireOverride({
                    action: 'waive_deposit',
                    title: 'Manager Approval to Waive Deposit',
                    description: `Waiving required deposit of ${formatCurrency(depositAfterCreate.maxAmount)}`,
                    context: {
                      ticketId: depositAfterCreate.id,
                      amount: depositAfterCreate.maxAmount,
                    },
                  });
                  if (!result.approved) return false;
                  await updateTicket(depositAfterCreate.id, {
                    depositSkipped: true,
                    managerOverrideAuthorizedBy: result.authorizedBy.id,
                  });
                  toastInfo('Deposit skipped — manager override');
                  void queryClient.invalidateQueries({ queryKey: ['ticket'] });
                  void queryClient.invalidateQueries({ queryKey: ['tickets'] });
                  const id = depositAfterCreate.id;
                  setDepositAfterCreate(null);
                  skipIdleOnUnmountRef.current = true;
                  maybePrintLabelAfterIntake(id);
                  navigate(`/tickets/${id}`, { state: { openPreWaiver: true } });
                  return true;
                }
              : undefined
          }
          ticketId={depositAfterCreate.id}
          ticketNumber={depositAfterCreate.ticketNumber}
          maxAmount={depositAfterCreate.maxAmount}
          customerHasStoreCredit={Number(selectedCustomer?.storeCreditBalance ?? 0) > 0}
          onClose={() => {
            pendingLabelPrintRef.current = false;
            setDepositAfterCreate(null);
          }}
          onSuccess={() => {
            void queryClient.invalidateQueries({ queryKey: ['ticket'] });
            void queryClient.invalidateQueries({ queryKey: ['tickets'] });
            const id = depositAfterCreate.id;
            setDepositAfterCreate(null);
            skipIdleOnUnmountRef.current = true;
            maybePrintLabelAfterIntake(id);
            navigate(`/tickets/${id}`, { state: { openPreWaiver: true } });
          }}
        />
      ) : null}
      {managerOverrideModal ? <ManagerOverrideModal {...managerOverrideModal} /> : null}
    </div>
  );
}
